import React from 'react';
import { View, Text, StyleSheet, NodeProps } from '@react-pdf/renderer';

import colors from '@/design/colors';
import DocumentMarkdown from '../DocumentMarkdown';

export type ReactPDFStyle = Exclude<NodeProps['style'], unknown[]>;

const styles = StyleSheet.create({
  panel: {
    border: `1px solid ${colors.gray100}`,
    flexDirection: 'column',
  },
  heading: {
    backgroundColor: colors.gray50,
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderBottom: `1px solid ${colors.gray100}`,

    fontSize: 10,
    fontWeight: 700,
  },
  content: {
    padding: 16,

    fontSize: 9,
    fontWeight: 400,
  },
});

type PanelStyle = {
  panel: ReactPDFStyle;
  content: ReactPDFStyle;
  heading: ReactPDFStyle;
};

interface PanelProps {
  title: string;
  content?: React.ReactNode;
  style?: Partial<PanelStyle>;
  wrap?: boolean;
  debug?: boolean;
}

const DocumentPanel: React.FC<PanelProps> = ({
  title,
  content,
  wrap = true,
  debug = false,
  style: {
    panel: panelStyle = {},
    heading: headingStyle = {},
    content: contentStyle = {},
  } = {},
}) => (
  <View wrap={wrap} debug={debug} style={[styles.panel, panelStyle]}>
    <View style={[styles.heading, headingStyle]} wrap={false}>
      <Text>{title}</Text>
    </View>
    <View style={[styles.content, contentStyle]}>
      {React.isValidElement(content) ? (
        content
      ) : (
        <DocumentMarkdown text={content as string} />
      )}
    </View>
  </View>
);

const primaryStyles = StyleSheet.create({
  panel: {
    borderWidth: 2,
    borderRadius: 4,
  },
  heading: {
    fontSize: 12,
    fontWeight: 700,
    borderBottomWidth: 2,
  },
});

export const DocumentPanelPrimary: React.FC<PanelProps> = ({
  title,
  content,
  style = {},
  wrap = false,
  debug = false,
}) => (
  <DocumentPanel
    debug={debug}
    wrap={wrap}
    title={title}
    content={content}
    style={{
      heading: {
        ...primaryStyles.heading,
        ...style.heading,
      },
      panel: { ...primaryStyles.panel, ...style.panel },
      content: { ...style.content },
    }}
  />
);

export default DocumentPanel;
